// Lightweight i18n framework. The active locale comes from the company's
// `language` setting (loaded into the auth store at session start). Strings are
// keyed (e.g. 'common.save') and translated incrementally — any missing key
// falls back to English, then to the key itself, so partial coverage is safe.
import { useAuthStore } from '@/shared/store/authStore';

export const LOCALES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
];

type Dict = Record<string, string>;

// English is the base/reference dictionary. Other locales need only cover the
// keys they translate; gaps fall back to English.
const en: Dict = {
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.new': 'New',
  'common.search': 'Search',
  'common.status': 'Status',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'nav.dashboard': 'Dashboard',
  'nav.sales': 'Sales',
  'nav.purchasing': 'Purchasing',
  'nav.inventory': 'Inventory',
  'nav.products': 'Products',
  'settings.title': 'Current Company Settings',
  'settings.language': 'Language',
  'settings.decimals': 'Decimal places',
  'settings.dateFormat': 'Date format',
};

const translations: Record<string, Dict> = {
  en,
  es: {
    'common.save': 'Guardar', 'common.cancel': 'Cancelar', 'common.delete': 'Eliminar',
    'common.edit': 'Editar', 'common.new': 'Nuevo', 'common.search': 'Buscar',
    'common.status': 'Estado', 'common.total': 'Total', 'common.actions': 'Acciones',
    'nav.dashboard': 'Panel', 'nav.sales': 'Ventas', 'nav.purchasing': 'Compras',
    'nav.inventory': 'Inventario', 'nav.products': 'Productos',
    'settings.title': 'Configuración de la empresa', 'settings.language': 'Idioma',
    'settings.decimals': 'Decimales', 'settings.dateFormat': 'Formato de fecha',
  },
  fr: {
    'common.save': 'Enregistrer', 'common.cancel': 'Annuler', 'common.delete': 'Supprimer',
    'common.edit': 'Modifier', 'common.new': 'Nouveau', 'common.search': 'Rechercher',
    'common.status': 'Statut', 'common.total': 'Total', 'common.actions': 'Actions',
    'nav.dashboard': 'Tableau de bord', 'nav.sales': 'Ventes', 'nav.purchasing': 'Achats',
    'nav.inventory': 'Inventaire', 'nav.products': 'Produits',
    'settings.title': "Paramètres de l'entreprise", 'settings.language': 'Langue',
    'settings.decimals': 'Décimales', 'settings.dateFormat': 'Format de date',
  },
  de: {
    'common.save': 'Speichern', 'common.cancel': 'Abbrechen', 'common.delete': 'Löschen',
    'common.edit': 'Bearbeiten', 'common.new': 'Neu', 'common.search': 'Suchen',
    'common.status': 'Status', 'common.total': 'Summe', 'common.actions': 'Aktionen',
    'nav.dashboard': 'Übersicht', 'nav.sales': 'Verkauf', 'nav.purchasing': 'Einkauf',
    'nav.inventory': 'Lager', 'nav.products': 'Produkte',
    'settings.title': 'Unternehmenseinstellungen', 'settings.language': 'Sprache',
    'settings.decimals': 'Dezimalstellen', 'settings.dateFormat': 'Datumsformat',
  },
  pt: {
    'common.save': 'Salvar', 'common.cancel': 'Cancelar', 'common.delete': 'Excluir',
    'common.edit': 'Editar', 'common.new': 'Novo', 'common.search': 'Pesquisar',
    'common.status': 'Situação', 'common.total': 'Total', 'common.actions': 'Ações',
    'nav.dashboard': 'Painel', 'nav.sales': 'Vendas', 'nav.purchasing': 'Compras',
    'nav.inventory': 'Estoque', 'nav.products': 'Produtos',
    'settings.title': 'Configurações da empresa', 'settings.language': 'Idioma',
    'settings.decimals': 'Casas decimais', 'settings.dateFormat': 'Formato de data',
  },
};

let activeLocale = 'en';

export function setLocale(locale?: string | null): void {
  activeLocale = locale && translations[locale] ? locale : 'en';
  if (typeof document !== 'undefined') document.documentElement.lang = activeLocale;
}

export function translate(key: string, locale: string = activeLocale): string {
  const loc = translations[locale] ? locale : 'en';
  return translations[loc][key] ?? translations.en[key] ?? key;
}

// Hook form: re-renders when the company language changes.
export function useT(): (key: string) => string {
  const lang = useAuthStore((s) => s.company?.language) || 'en';
  return (key: string) => translate(key, lang);
}
